import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PRIVACY_POLICY_PATH } from '../constants/legalPaths';

const STORAGE_KEY = 'noel-cookie-consent-v1';
export const COOKIE_SETTINGS_EVENT = 'noel-open-cookie-settings';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && typeof p === 'object') return p;
  } catch (_) {
    /* ignore */
  }
  return null;
}

function writeStored(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefs, v: 1, at: Date.now() }));
  } catch (_) {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('noel-cookie-consent-updated', { detail: prefs }));
}

export default function CookieConsent() {
  const { t } = useI18n();
  const [bannerVisible, setBannerVisible] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [functional, setFunctional] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(false);
  const [advertising, setAdvertising] = React.useState(false);

  const applyFromStorage = React.useCallback((saved) => {
    if (!saved) return;
    setFunctional(!!saved.functional);
    setAnalytics(!!saved.analytics);
    setAdvertising(!!saved.advertising);
  }, []);

  React.useEffect(() => {
    const saved = readStored();
    if (!saved) setBannerVisible(true);
    else applyFromStorage(saved);
  }, [applyFromStorage]);

  React.useEffect(() => {
    const open = () => {
      const saved = readStored();
      if (saved) applyFromStorage(saved);
      setModalOpen(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, [applyFromStorage]);

  React.useEffect(() => {
    document.body.classList.toggle('cookie-modal-open', modalOpen);
    return () => document.body.classList.remove('cookie-modal-open');
  }, [modalOpen]);

  React.useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const persist = (f, a, adv) => {
    writeStored({ necessary: true, functional: f, analytics: a, advertising: adv });
    setBannerVisible(false);
    setModalOpen(false);
  };

  const acceptAll = () => {
    setFunctional(true);
    setAnalytics(true);
    setAdvertising(true);
    persist(true, true, true);
  };

  const rejectNonEssential = () => {
    setFunctional(false);
    setAnalytics(false);
    setAdvertising(false);
    persist(false, false, false);
  };

  const saveSettings = () => persist(functional, analytics, advertising);

  return (
    <>
      {bannerVisible && (
        <div
          className="cookie-banner"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-desc"
        >
          <div className="cookie-banner-card">
            <h2 id="cookie-banner-title" className="cookie-banner-title">
              {t('cookie_banner_title')}
            </h2>
            <p id="cookie-banner-desc" className="cookie-banner-text">
              {t('cookie_banner_body')}{' '}
              <Link to={PRIVACY_POLICY_PATH} className="cookie-banner-inline-link">
                {t('cookie_banner_privacy_link')}
              </Link>
              .
            </p>
            <div className="cookie-banner-actions">
              <button type="button" className="cookie-btn cookie-btn--outline" onClick={() => setModalOpen(true)}>
                {t('cookie_customize')}
              </button>
              <button type="button" className="cookie-btn cookie-btn--solid cookie-btn--muted" onClick={rejectNonEssential}>
                {t('cookie_reject')}
              </button>
              <button type="button" className="cookie-btn cookie-btn--solid" onClick={acceptAll}>
                {t('cookie_accept_all')}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="cookie-modal-backdrop" role="presentation" onClick={() => setModalOpen(false)}>
          <div
            className="cookie-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-prefs-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cookie-modal-head">
              <h2 id="cookie-prefs-title" className="cookie-modal-title">
                {t('cookie_prefs_title')}
              </h2>
              <button type="button" className="cookie-modal-close" onClick={() => setModalOpen(false)} aria-label={t('cookie_close')}>
                ×
              </button>
            </div>
            <p className="cookie-modal-intro">{t('cookie_prefs_intro')}</p>

            <ul className="cookie-cat-list">
              <li className="cookie-cat">
                <div className="cookie-cat-head">
                  <span className="cookie-cat-name">{t('cookie_cat_necessary')}</span>
                  <span className="cookie-cat-badge">{t('cookie_always_active')}</span>
                </div>
                <p className="cookie-cat-desc">{t('cookie_cat_necessary_desc')}</p>
              </li>
              <li className="cookie-cat">
                <div className="cookie-cat-head">
                  <span className="cookie-cat-name">{t('cookie_cat_functional')}</span>
                  <label className="cookie-toggle">
                    <input type="checkbox" checked={functional} onChange={(e) => setFunctional(e.target.checked)} />
                    <span className="cookie-toggle-ui" aria-hidden="true" />
                  </label>
                </div>
                <p className="cookie-cat-desc">{t('cookie_cat_functional_desc')}</p>
              </li>
              <li className="cookie-cat">
                <div className="cookie-cat-head">
                  <span className="cookie-cat-name">{t('cookie_cat_analytics')}</span>
                  <label className="cookie-toggle">
                    <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} />
                    <span className="cookie-toggle-ui" aria-hidden="true" />
                  </label>
                </div>
                <p className="cookie-cat-desc">{t('cookie_cat_analytics_desc')}</p>
              </li>
              <li className="cookie-cat">
                <div className="cookie-cat-head">
                  <span className="cookie-cat-name">{t('cookie_cat_ads')}</span>
                  <label className="cookie-toggle">
                    <input type="checkbox" checked={advertising} onChange={(e) => setAdvertising(e.target.checked)} />
                    <span className="cookie-toggle-ui" aria-hidden="true" />
                  </label>
                </div>
                <p className="cookie-cat-desc">{t('cookie_cat_ads_desc')}</p>
              </li>
            </ul>

            <div className="cookie-modal-actions">
              <button type="button" className="cookie-btn cookie-btn--solid cookie-btn--muted" onClick={rejectNonEssential}>
                {t('cookie_reject')}
              </button>
              <button type="button" className="cookie-btn cookie-btn--outline" onClick={saveSettings}>
                {t('cookie_save')}
              </button>
              <button type="button" className="cookie-btn cookie-btn--solid" onClick={acceptAll}>
                {t('cookie_accept_all')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
